import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Archive,
  HardDrive,
  Image,
  TreePine,
  Users,
} from 'lucide-react';
import apiClient from '@/api/client';
import { fetchBaumUebersicht, fetchKontenUebersicht } from '@/api/betrieb.api';
import { bytes, datum, zahl, zeitpunkt } from '@/components/format';

// ─── Übersicht der Installation ──────────────────────────────────────────────
//
// Wird überflogen, nicht gelesen: erst die Kennzahlen, dann das, was
// Aufmerksamkeit braucht. Auffällig ist nur, was auffällig sein soll – eine
// fehlgeschlagene Sicherung sticht heraus, alles andere bleibt ruhig.

interface Kennzahlen {
  persons: number;
  relationships: number;
  media: { count: number; totalBytes: number };
  users: { total: number; active: number };
  backup: {
    lastRunAt: string | null;
    lastStatus: 'success' | 'error' | null;
    nextRunAt: string | null;
  };
  storage: { usedBytes: number; fileCount: number };
  recentActivity: { action: string; userEmail: string | null; createdAt: string }[];
}

function Kachel({
  icon: Icon,
  label,
  wert,
  zusatz,
}: {
  icon: typeof Users;
  label: string;
  wert: string;
  zusatz?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">{wert}</p>
      {zusatz && <p className="mt-0.5 text-xs text-slate-500">{zusatz}</p>}
    </div>
  );
}

export function DashboardPage() {
  const { data, isLoading, error } = useQuery<Kennzahlen>({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => (await apiClient.get('/admin/dashboard')).data,
    refetchInterval: 60_000,
  });

  // Zwei weitere Abfragen statt einer großen: jede Liste hat ihre eigene
  // Ladezeit, und eine langsame soll die Kennzahlen oben nicht aufhalten.
  const baeume = useQuery({
    queryKey: ['admin', 'trees-overview'],
    queryFn: fetchBaumUebersicht,
    refetchInterval: 60_000,
  });
  const konten = useQuery({
    queryKey: ['admin', 'accounts-overview'],
    queryFn: fetchKontenUebersicht,
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-slate-500 sm:p-6">Kennzahlen werden geladen…</div>;
  }

  if (error || !data) {
    return (
      <div className="p-4 sm:p-6">
        <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error instanceof Error ? error.message : 'Kennzahlen konnten nicht geladen werden.'}
        </p>
      </div>
    );
  }

  const sicherungFehlgeschlagen = data.backup.lastStatus === 'error';

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 sm:space-y-6 sm:p-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Übersicht</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Stand dieser Installation. Aktualisiert sich jede Minute.
        </p>
      </header>

      {/* Zuerst das, was Aufmerksamkeit braucht */}
      {sicherungFehlgeschlagen && (
        <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm leading-relaxed text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Die letzte automatische Sicherung ist fehlgeschlagen ({zeitpunkt(data.backup.lastRunAt)}
            ). Bis das behoben ist, gibt es keinen aktuellen Stand auf der Storage Box.
          </span>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kachel icon={Users} label="Konten" wert={String(data.users.total)}
          zusatz={`${data.users.active} aktiv`} />
        <Kachel icon={TreePine} label="Personen" wert={data.persons.toLocaleString('de-DE')}
          zusatz={`${data.relationships.toLocaleString('de-DE')} Beziehungen`} />
        <Kachel icon={Image} label="Archiv" wert={String(data.media.count)}
          zusatz={bytes(data.media.totalBytes)} />
        <Kachel icon={HardDrive} label="Belegter Speicher" wert={bytes(data.storage.usedBytes)}
          zusatz={`${data.storage.fileCount} Dateien auf der Platte`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Sicherung */}
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Archive className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Automatische Sicherung
          </h2>
          <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-slate-500">Zuletzt</dt>
            <dd className="text-slate-800">
              {zeitpunkt(data.backup.lastRunAt)}
              {data.backup.lastStatus && (
                <span
                  className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    data.backup.lastStatus === 'success'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {data.backup.lastStatus === 'success' ? 'erfolgreich' : 'fehlgeschlagen'}
                </span>
              )}
            </dd>
            <dt className="text-slate-500">Als Nächstes</dt>
            <dd className="text-slate-800">{zeitpunkt(data.backup.nextRunAt)}</dd>
          </dl>
        </section>

        {/* Letzte Ereignisse */}
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Activity className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Zuletzt geschehen
          </h2>
          {data.recentActivity.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Noch nichts protokolliert.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {data.recentActivity.map((e, i) => (
                <li key={`${e.action}-${e.createdAt}-${i}`} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                    {e.action}
                  </code>
                  <span className="min-w-0 truncate text-slate-600">{e.userEmail ?? 'System'}</span>
                  <span className="ml-auto shrink-0 text-xs text-slate-400">
                    {zeitpunkt(e.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* ─── Stammbäume nach Umfang ──────────────────────────────────── */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Stammbäume</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Nach Umfang sortiert. Ein Baum ohne Besitzer lässt sich von niemandem
            verwalten – unter Stammbäume nachtragen.
          </p>
        </div>

        {baeume.isLoading && (
          <p className="px-4 py-3 text-sm text-slate-500">Wird geladen…</p>
        )}

        {baeume.data && baeume.data.length === 0 && (
          <p className="px-4 py-3 text-sm text-slate-500">Kein Stammbaum vorhanden.</p>
        )}

        {baeume.data && baeume.data.length > 0 && (
          // Tabellen sind am Handy zu breit; ein eigener Scrollbereich hält die
          // Seite schmal, statt sie seitwärts zu schieben.
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 text-right font-medium">Personen</th>
                  <th className="px-4 py-2 text-right font-medium">Beziehungen</th>
                  <th className="px-4 py-2 text-right font-medium">Medien</th>
                  <th className="px-4 py-2 text-right font-medium">Mitglieder</th>
                  <th className="px-4 py-2 font-medium">Besitzer</th>
                  <th className="px-4 py-2 font-medium">Angelegt</th>
                </tr>
              </thead>
              <tbody>
                {baeume.data.map((b) => (
                  <tr key={b.id} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-2 font-medium text-slate-900">{b.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                      {zahl(b.personen)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                      {zahl(b.beziehungen)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                      {zahl(b.medien)}
                      <span className="ml-1 text-xs text-slate-400">{bytes(b.medienBytes)}</span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                      {zahl(b.mitglieder)}
                    </td>
                    <td className="px-4 py-2">
                      {b.besitzer ? (
                        <span className="text-slate-700">{b.besitzer}</span>
                      ) : (
                        <span className="font-medium text-red-700">keiner</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-500">{datum(b.angelegtAm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ─── Konten ──────────────────────────────────────────────────── */}
      {konten.data && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Users className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Konten
          </h2>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Zaehler label="Aktiv" wert={konten.data.aktiv} von={konten.data.gesamt} />
            <Zaehler label="Betreiber" wert={konten.data.betreiber} />
            <Zaehler
              label="Noch nicht freigeschaltet"
              wert={konten.data.wartendAufFreischaltung}
              auffaellig={konten.data.wartendAufFreischaltung > 0}
            />
            <Zaehler label="Mit Zwei-Faktor" wert={konten.data.mitZweiFaktor} von={konten.data.gesamt} />
          </div>

          {konten.data.neueste.length > 0 && (
            <>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Zuletzt angelegt
              </p>
              <ul className="mt-2 space-y-1.5">
                {konten.data.neueste.map((k) => (
                  <li key={k.email} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span className="min-w-0 truncate text-slate-700">{k.email}</span>
                    {!k.aktiv && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
                        nicht freigeschaltet
                      </span>
                    )}
                    <span className="ml-auto shrink-0 text-xs text-slate-400">
                      {datum(k.angelegtAm)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function Zaehler({
  label,
  wert,
  von,
  auffaellig,
}: {
  label: string;
  wert: number;
  von?: number;
  auffaellig?: boolean;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p
        className={`mt-1 text-xl font-semibold tabular-nums ${
          auffaellig ? 'text-amber-600' : 'text-slate-900'
        }`}
      >
        {zahl(wert)}
        {von !== undefined && <span className="text-sm font-normal text-slate-400"> / {zahl(von)}</span>}
      </p>
    </div>
  );
}
